import ignore, { Ignore } from 'ignore';
import { URI, Utils } from 'vscode-uri';
import { getRelativePath } from '../fs/utils';
import { FastDirectoryMatcher } from '../fs/dir_matcher';

/**
 * GitIgnoreManager aims to be a spec compliant gitignore manager.
 * It uses the ignore package to match the file against gitignore patterns.
 * It also checks the .gitignore/exclude file in the root of the git repository.
 *
 * A majority of this logic has been adapted from https://github.com/isomorphic-git/isomorphic-git
 *
 * We opted for using our own implementation for both speed and flexibility.
 *
 * This class is stateful: it maintains an internal state of the gitignore
 * files that have been added to the manager. Be sure to update the manager
 * as gitignore files are added, updated, or removed.
 *
 */
export class GitIgnoreManager {
  #rootUri: URI;

  #matcher: FastDirectoryMatcher;

  #excludeFileIgnore?: Ignore;

  #ignoreMap: Map<string, Ignore>;

  /**
   * Create a new GitIgnoreManager.
   *
   * @param rootUri The root URI of the git repository.
   * @param excludeFileContent (optional) The content of the exclude file in the root of the git repository.
   */
  constructor(rootUri: URI) {
    this.#rootUri = rootUri;
    this.#matcher = new FastDirectoryMatcher();
    this.#ignoreMap = new Map();
  }

  /**
   * Add a top level exclude file to the manager.
   * This is typically located at .git/info/exclude.
   */
  addExcludeFile(content: string): void {
    this.#excludeFileIgnore = ignore().add(content);
  }

  /**
   * Add a gitignore file to the manager.
   * This will add the gitignore file to the ignore map and parse the patterns.
   */
  addGitignore(uri: URI, content: string): void {
    const ig = ignore().add(content);
    this.#matcher.addFileToMatch(uri);
    this.#ignoreMap.set(Utils.dirname(uri).toString(), ig);
  }

  /**
   * Check if the given file URI is ignored by git.
   * This will match the file against gitignore patterns in the directory structure
   * and all its parent directories.
   *
   * This also checks the .gitignore/exclude file in the root of the git repository.
   */
  isIgnored(filepath: URI): boolean {
    // ALWAYS ignore ".git" folders.
    if (Utils.basename(filepath) === '.git') return true;

    // '.' is not a valid gitignore entry, so '.' is never ignored
    if (filepath.path === '.') return false;

    const matchingDirs = this.#matcher.findMatchingDirectories(filepath);

    let ignoredStatus =
      this.#excludeFileIgnore?.ignores(getRelativePath(this.#rootUri, filepath)) ?? false;

    for (const dir of matchingDirs) {
      const ig = this.#ignoreMap.get(dir.toString());
      if (!ig) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const { ignored, unignored } = ig.test(getRelativePath(dir, filepath));
      ignoredStatus = ignoredStatus ? !unignored : ignored;
    }

    return ignoredStatus;
  }

  dispose(): void {
    this.#matcher.dispose();
  }
}
